import { useState } from 'react'
import type { FormEvent } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { FirebaseError } from 'firebase/app'
import { useAuth } from '../lib/auth'
import { ApiError } from '../lib/api'
import { Alert, Button, Input, PasswordInput } from '../components/ui'
import { Logo } from '../components/Logo'
import './auth.css'

/* Firebase and the backend both fail in ways a miner in the field cannot act
 * on ("auth/invalid-credential"). Translate to something actionable. */
function readableError(err: unknown): string {
  if (err instanceof FirebaseError) {
    switch (err.code) {
      case 'auth/invalid-credential':
      case 'auth/wrong-password':
      case 'auth/user-not-found':
        return 'That email or password is not correct.'
      case 'auth/invalid-email':
        return 'That does not look like a valid email address.'
      case 'auth/user-disabled':
        return 'This account has been disabled. Contact your administrator.'
      case 'auth/too-many-requests':
        return 'Too many attempts. Wait a few minutes and try again.'
      case 'auth/network-request-failed':
        return 'Cannot reach the server. Check your internet connection.'
      default:
        return 'Sign-in failed. Please try again.'
    }
  }
  if (err instanceof ApiError) {
    if (err.code === 'profile_not_found') {
      return 'Your sign-in worked, but no SSM profile is linked to this account. Contact your administrator.'
    }
    if (err.code === 'suspended') return 'This account is suspended. Contact your administrator.'
    return err.message
  }
  return 'Something went wrong. Please try again.'
}

export default function Login() {
  const { signIn } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setError('')
    setBusy(true)
    try {
      await signIn(email, password)
      const from = (location.state as { from?: string } | null)?.from
      navigate(from && from !== '/login' ? from : '/', { replace: true })
    } catch (err) {
      setError(readableError(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="auth">
      <aside className="auth__brand">
        <svg className="auth__contours" aria-hidden preserveAspectRatio="none" viewBox="0 0 400 600">
          {Array.from({ length: 11 }, (_, i) => (
            <path
              key={i}
              d={`M-20 ${70 + i * 48} C 90 ${20 + i * 48}, 190 ${132 + i * 48}, 420 ${52 + i * 48}`}
              fill="none" stroke="#fff" strokeWidth="1"
            />
          ))}
        </svg>

        <div className="auth__brand-top"><Logo tone="onDark" tagline="Compliance & Safety" /></div>

        <div className="auth__brand-copy">
          <h2>Compliance, safety and training for Zambia&rsquo;s small-scale miners.</h2>
          <p>
            Register sites, log production, file safety reports and track training
            &mdash; from the field or the office.
          </p>
        </div>

        <div className="auth__stats">
          <div>
            <div className="auth__stat-n">10</div>
            <div className="auth__stat-l">Provinces</div>
          </div>
          <div>
            <div className="auth__stat-n">24/7</div>
            <div className="auth__stat-l">Reporting</div>
          </div>
          <div>
            <div className="auth__stat-n">APK</div>
            <div className="auth__stat-l">Android app</div>
          </div>
        </div>

        <a className="store-badge store-badge--aside" href="/downloads/ssm-zambia.apk" download>
          <svg className="store-badge__icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
            <path d="M12 2a1 1 0 0 1 1 1v10.6l3.3-3.3a1 1 0 1 1 1.4 1.4l-5 5a1 1 0 0 1-1.4 0l-5-5a1 1 0 1 1 1.4-1.4l3.3 3.3V3a1 1 0 0 1 1-1z" />
            <path d="M5 19a1 1 0 0 1 1-1h12a1 1 0 1 1 0 2H6a1 1 0 0 1-1-1z" />
          </svg>
          <span className="store-badge__text">
            <span className="store-badge__kicker">Download for Android</span>
            <span className="store-badge__name">SSM Zambia App</span>
          </span>
        </a>
      </aside>

      <main className="auth__panel">
        <div className="auth__form">
          <div className="auth__mobile-brand"><Logo /></div>

          <h1 className="auth__title">Welcome back</h1>
          <p className="auth__sub">Sign in to your SSM Zambia account.</p>

          <form onSubmit={onSubmit} noValidate>
            <div className="auth__fields">
              {error && <Alert tone="danger">{error}</Alert>}

              <Input
                label="Email address"
                type="email"
                inputMode="email"
                autoComplete="email"
                autoFocus
                required
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />

              <PasswordInput
                label="Password"
                autoComplete="current-password"
                required
                placeholder="Your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />

              <div className="auth__row">
                <span />
                <Link to="/forgot-password">Forgot password?</Link>
              </div>

              <Button type="submit" size="lg" block loading={busy}>
                {busy ? 'Signing in…' : 'Sign in'}
              </Button>
            </div>
          </form>

          <p className="auth__foot">
            Need an account? Registration is handled by your cooperative
            administrator or the Ministry.
          </p>
          <p className="auth__foot">
            Registering a new company or cooperative?{' '}
            <a href="/public/company-signup">Start here</a>.
          </p>
          {/* Store-style badge, deliberately NOT dressed as Google Play -
              the app is a direct download from this site, and borrowing
              Play's branding for a link that never goes near Play would
              mislead. Served straight from Hosting (web/public/downloads),
              so it works logged-out. */}
          <a className="store-badge" href="/downloads/ssm-zambia.apk" download>
            <svg className="store-badge__icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
              <path d="M12 2a1 1 0 0 1 1 1v10.6l3.3-3.3a1 1 0 1 1 1.4 1.4l-5 5a1 1 0 0 1-1.4 0l-5-5a1 1 0 1 1 1.4-1.4l3.3 3.3V3a1 1 0 0 1 1-1z" />
              <path d="M5 19a1 1 0 0 1 1-1h12a1 1 0 1 1 0 2H6a1 1 0 0 1-1-1z" />
            </svg>
            <span className="store-badge__text">
              <span className="store-badge__kicker">Download for Android</span>
              <span className="store-badge__name">SSM Zambia App</span>
            </span>
          </a>
        </div>
      </main>
    </div>
  )
}
